import { ResizeDirection } from '../types';
/**
 * Manages styling for widgets
 */
export class WidgetStyles {
    constructor(theme) {
        Object.defineProperty(this, "theme", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "resizeHandleSize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 8
        }); // Size of invisible resize handles in pixels
        this.theme = theme;
    }
    /**
     * Generates container styles
     * @param position Widget position
     * @param size Widget size
     * @returns CSS string for container
     */
    getContainerStyles(position, size) {
        const { backgroundColor, borderRadius } = this.theme;
        return `
      position: fixed;
      top: ${position.y}px;
      left: ${position.x}px;
      width: ${size.width}px;
      height: ${size.height}px;
      background: ${backgroundColor};
      border: 1px solid #ccc;
      border-radius: ${borderRadius};
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      overflow: hidden;
      z-index: 999999;
      display: flex;
      flex-direction: column;
      will-change: transform, width, height;
      box-sizing: border-box;
    `;
    }
    /**
     * Generates drag handle styles
     * @param draggable Whether the widget is draggable
     * @returns CSS string for drag handle
     */
    getDragHandleStyles(draggable = true) {
        const { secondaryColor, textColor, fontFamily, fontSize, borderRadius } = this.theme;
        return `
      height: 32px;
      width: 100%;
      background: ${secondaryColor};
      border-bottom: 1px solid #e0e0e0;
      cursor: ${draggable ? 'move' : 'default'};
      display: flex;
      align-items: center;
      padding: 0 12px;
      box-sizing: border-box;
      border-top-left-radius: ${borderRadius};
      border-top-right-radius: ${borderRadius};
      user-select: none;
      position: relative;
      font-family: ${fontFamily};
      font-size: ${fontSize};
      color: ${textColor};
    `;
    }
    /**
     * Generates title text styles
     * @returns CSS string for title
     */
    getTitleStyles() {
        return `
      font-weight: 500;
      flex: 1;
    `;
    }
    /**
     * Generates close button styles
     * @returns CSS string for close button
     */
    getCloseButtonStyles() {
        return `
      cursor: pointer;
      padding: 0 6px;
      font-size: 16px;
      opacity: 0.7;
    `;
    }
    /**
     * Generates fullscreen button styles
     * @returns CSS string for fullscreen button
     */
    getFullscreenButtonStyles() {
        return `
      cursor: pointer;
      padding: 0 6px;
      font-size: 14px;
      opacity: 0.7;
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
    `;
    }
    /**
     * Generates minimize button styles
     * @returns CSS string for minimize button
     */
    getMinimizeButtonStyles() {
        return `
      cursor: pointer;
      padding: 0 6px;
      font-size: 14px;
      opacity: 0.7;
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
    `;
    }
    /**
     * Generates content container styles
     * @returns CSS string for content container
     */
    getContentStyles() {
        return `
      flex: 1;
      overflow: auto;
      position: relative;
    `;
    }
    /**
     * Generates resize handle styles
     * @returns CSS string for resize handle
     */
    getResizeHandleStyles() {
        return `
      position: absolute;
      bottom: 0;
      right: 0;
      width: 16px;
      height: 16px;
      cursor: se-resize;
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
      padding: 3px;
      box-sizing: border-box;
    `;
    }
    /**
     * Generates styles for invisible resize handles
     * @param direction The resize direction
     * @returns CSS string for the resize handle
     */
    getInvisibleResizeHandleStyles(direction) {
        const size = this.resizeHandleSize;
        // Base styles
        let styles = `
      position: absolute;
      background: transparent;
      z-index: 1000000;
    `;
        // Position and cursor based on direction
        switch (direction) {
            case ResizeDirection.N:
                styles += `
          top: 0;
          left: ${size}px;
          right: ${size}px;
          height: ${size}px;
          cursor: ns-resize;
        `;
                break;
            case ResizeDirection.E:
                styles += `
          top: ${size}px;
          right: 0;
          bottom: ${size}px;
          width: ${size}px;
          cursor: ew-resize;
        `;
                break;
            case ResizeDirection.S:
                styles += `
          bottom: 0;
          left: ${size}px;
          right: ${size}px;
          height: ${size}px;
          cursor: ns-resize;
        `;
                break;
            case ResizeDirection.W:
                styles += `
          top: ${size}px;
          left: 0;
          bottom: ${size}px;
          width: ${size}px;
          cursor: ew-resize;
        `;
                break;
            case ResizeDirection.NE:
                styles += `
          top: 0;
          right: 0;
          width: ${size}px;
          height: ${size}px;
          cursor: nesw-resize;
        `;
                break;
            case ResizeDirection.SE:
                styles += `
          bottom: 0;
          right: 0;
          width: ${size}px;
          height: ${size}px;
          cursor: nwse-resize;
        `;
                break;
            case ResizeDirection.SW:
                styles += `
          bottom: 0;
          left: 0;
          width: ${size}px;
          height: ${size}px;
          cursor: nesw-resize;
        `;
                break;
            case ResizeDirection.NW:
                styles += `
          top: 0;
          left: 0;
          width: ${size}px;
          height: ${size}px;
          cursor: nwse-resize;
        `;
                break;
        }
        return styles;
    }
    /**
     * Generates resize grip styles
     * @returns CSS string for resize grip
     */
    getResizeGripStyles() {
        return `
      width: 10px;
      height: 10px;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(2, 1fr);
      gap: 1px;
    `;
    }
    /**
     * Generates resize grip dot styles
     * @returns CSS string for resize grip dots
     */
    getResizeGripDotStyles() {
        return `
      width: 3px;
      height: 3px;
      background-color: #888;
      border-radius: 50%;
    `;
    }
    /**
     * Generates styles for react root container
     * @returns CSS string for react root
     */
    getReactRootStyles() {
        return `
      width: 100%;
      height: 100%;
    `;
    }
}
//# sourceMappingURL=WidgetStyles.js.map